import { agentWritesApi } from '../../api/agents/agent-writes'
import type {
  InFlightAgentActionProposal,
  TagHistorizationAppliedResult,
  TagHistorizationOperation
} from '../../slices/agent-orchestrator-slice'
import {
  buildTagHistorizationEntryFromToolInput,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

export const PROPOSE_ENABLE_TAG_HISTORY_TOOL_NAME = 'propose_enable_tag_history'
export const PROPOSE_DISABLE_TAG_HISTORY_TOOL_NAME = 'propose_disable_tag_history'
export const PROPOSE_CHANGE_TAG_HISTORY_SOURCE_TOOL_NAME = 'propose_change_tag_history_source'

/** The propose step resolves the selection and previews before the row persists. */
const HYDRATE_RETRY_DELAYS_MS = [0, 400, 1200, 2500, 5000, 10_000, 20_000]

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function readTagHistorizationAppliedResult(
  result: Record<string, unknown> | undefined
): TagHistorizationAppliedResult | undefined {
  if (!result) return undefined
  const failed = Array.isArray(result.failed) ? result.failed : []
  return {
    registeredCount: count(result.registered_count),
    quarantinedCount: count(result.quarantined_count),
    enabledCount: count(result.enabled_count),
    disabledCount: count(result.disabled_count),
    convertedCount: count(result.converted),
    unchangedCount:
      count(result.already_recording_count) +
      count(result.already_off_count) +
      count(result.not_registered_count) +
      count(result.skipped),
    failed: failed.flatMap((row) =>
      row && typeof row === 'object' && !Array.isArray(row)
        ? [
            {
              tagPath: text((row as Record<string, unknown>).tag_path),
              reason: text((row as Record<string, unknown>).reason)
            }
          ]
        : []
    )
  }
}

/** Nonzero applied counts as phrases, shared by the settled card and the agent's resume line. */
export function tagHistorizationAppliedParts(result: TagHistorizationAppliedResult): string[] {
  return [
    result.registeredCount > 0
      ? `${result.registeredCount} registered${
          result.quarantinedCount > 0
            ? ` (${result.quarantinedCount} quarantined, not collecting yet)`
            : ''
        }`
      : '',
    result.enabledCount > 0 ? `${result.enabledCount} resumed` : '',
    result.disabledCount > 0 ? `${result.disabledCount} stopped` : '',
    result.convertedCount > 0 ? `${result.convertedCount} switched` : '',
    result.unchangedCount > 0 ? `${result.unchangedCount} unchanged` : ''
  ].filter(Boolean)
}

function sanitizeSummary(value: string): string {
  return value.replace(/"/g, '').trim()
}

function appliedOutcome(result: TagHistorizationAppliedResult | undefined): string {
  if (!result) return 'applied'
  const parts = tagHistorizationAppliedParts(result)
  const failures = result.failed
    .slice(0, 3)
    .map((failure) => `${failure.tagPath || 'tag'}: ${failure.reason}`)
  if (result.failed.length > 0) {
    parts.push(`${result.failed.length} failed (${failures.join('; ')})`)
  }
  return parts.length > 0 ? parts.join(', ') : 'nothing needed changing'
}

function lifecycleFor(
  toolName: string,
  operation: TagHistorizationOperation
): AgentActionLifecycleCapability {
  return {
    kind: 'tag_historization',
    toolName,

    parseToolStart(toolUseId, input, startedAtMs) {
      const entry = buildTagHistorizationEntryFromToolInput(
        toolUseId,
        operation,
        input,
        startedAtMs
      )
      return entry ? [entry] : []
    },

    parseToolComplete() {
      return null
    },

    parseSettledToolComplete(output) {
      return parseSettledProposeOutput(output)
    },

    scheduleHydratePending(dispatch, conversationId) {
      scheduleHydratePendingAgentWrites(dispatch, conversationId, HYDRATE_RETRY_DELAYS_MS)
    },

    apply(toolUseId) {
      return async (dispatch): Promise<AgentActionApplyOutcome> => {
        try {
          const response = await dispatch(
            agentWritesApi.endpoints.applyCypherWrite.initiate({
              toolUseId,
              graphAffecting: false
            })
          ).unwrap()
          if (response.ok) {
            const appliedResult = readTagHistorizationAppliedResult(response.result)
            return { kind: 'success', settledMeta: appliedResult ? { appliedResult } : {} }
          }
          return {
            kind: 'apply_failed',
            error: response.error ?? 'Tag history change failed.'
          }
        } catch (error) {
          const refused = readStructuredMutationError(error)
          if (refused) return { kind: 'apply_failed', error: refused.error }
          return {
            kind: 'transport_error',
            error: error instanceof Error ? error.message : 'Tag history change failed.'
          }
        }
      }
    },

    reject(toolUseId, reason) {
      return async (dispatch): Promise<AgentActionRejectOutcome> => {
        try {
          const response = await dispatch(
            agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
          ).unwrap()
          if (response.ok) {
            return {
              kind: 'success',
              settledMeta: { rejectionReason: response.reason ?? reason }
            }
          }
          return { kind: 'reject_failed', error: response.error ?? 'Reject failed.' }
        } catch (error) {
          return {
            kind: 'transport_error',
            error: error instanceof Error ? error.message : 'Reject failed.'
          }
        }
      }
    },

    buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
      const summary = sanitizeSummary(proposal.summary)
      if (proposal.kind !== 'tag_historization') {
        return `${displayNumber}. UNDECIDED — "${summary}".`
      }
      if (proposal.terminalState?.kind === 'applied') {
        return `${displayNumber}. APPLIED — "${summary}"; ${appliedOutcome(proposal.appliedResult)}.`
      }
      if (proposal.terminalState?.kind === 'apply_failed') {
        return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${proposal.terminalState.error}.`
      }
      if (proposal.terminalState?.kind === 'rejected') {
        return `${displayNumber}. REJECTED — "${summary}"; reason: "${proposal.rejectionReason ?? 'rejected by user'}"; nothing changed.`
      }
      return `${displayNumber}. UNDECIDED — "${summary}".`
    }
  }
}

/**
 * One registration per propose tool so the optimistic card knows its
 * operation; all three share the `tag_historization` kind and handlers.
 */
export const tagHistorizationLifecycleCapabilities: readonly AgentActionLifecycleCapability[] = [
  lifecycleFor(PROPOSE_ENABLE_TAG_HISTORY_TOOL_NAME, 'enable'),
  lifecycleFor(PROPOSE_DISABLE_TAG_HISTORY_TOOL_NAME, 'disable'),
  lifecycleFor(PROPOSE_CHANGE_TAG_HISTORY_SOURCE_TOOL_NAME, 'change_source')
]

for (const capability of tagHistorizationLifecycleCapabilities) {
  registerAgentActionLifecycle(capability)
}
